<template>
    <div class="segment-chart">
        <div class="toolbar">
            <div class="left">
                <div class="buttons">
                    <IconButton
                        :classNames="tableMode ? '' : 'selected'"
                        :onClick="() => (tableMode = false)"
                        v-tippy="{ content: 'Chart View' }"
                    >
                        <md-icon>insights</md-icon>
                    </IconButton>
                    <IconButton
                        :classNames="tableMode ? 'selected' : ''"
                        :onClick="() => (tableMode = true)"
                        v-tippy="{ content: 'Table View' }"
                    >
                        <md-icon>table_view</md-icon>
                    </IconButton>
                    <IconButton :onClick="exportToExcel" v-tippy="{ content: 'Download' }">
                        <md-icon>download</md-icon>
                    </IconButton>
                </div>
            </div>
            <div class="right"></div>
        </div>
        <TableView :cols="getCols()" :rows="getRows()" v-if="tableMode" />
        <StackedAreaChart
            :title="$t('report.segmentTimeseries')"
            :x-axis-data="getXAxisData()"
            :legends="legends.map((x) => x.legend)"
            :colors="colors"
            :series="getSeries()"
            v-else
        />

        <div class="stats">
            <StatCard
                :key="key"
                :title="getSegmentNameById(key)"
                :value="stats.segmentCounts[key]"
                v-for="key of Object.keys(stats.segmentCounts)"
                :unit="$t('report.users')"
            />
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import StackedAreaChart from "./StackedAreaChart.vue";
import moment from "moment";
import * as echarts from "echarts/lib/echarts";
import _ from "lodash";
import StatCard from "./StatCard.vue";
import IconButton from "../../../../Settings/components/IconButton.vue";
import TableView from "./TableView.vue";
import { expertToExcel } from "../../../../../../utils/utils";

export default Vue.extend({
    name: "SegmentChart",
    components: { TableView, IconButton, StatCard, StackedAreaChart },
    props: {
        data: {
            type: Object,
            required: true,
        },
        segments: {
            type: Array,
            required: true,
        },
    },
    data() {
        const distinctSegments = this.segments.map((segment, i) => {
            segment.legend = `${i + 1}-${segment.name}`;
            return segment;
        });
        const colorList = [
            "#80FFA5",
            "#00DDFF",
            "#37A2FF",
            "#FF0087",
            "#FFBF00",
            "#EC3131",
            "#C070FF",
            "#FFA500",
            "#A993FF",
            "#FF6B6B",
        ];
        return {
            colorList,
            colors: distinctSegments.map((x, i) => colorList[i % colorList.length]),
            legends: distinctSegments,
            stats: this.getStats(),
            tableMode: false,
        };
    },
    methods: {
        getCols() {
            return [
                {
                    title: this.$t("chatbotv2.date"),
                    dataIndex: "date",
                    key: "date",
                },
                ...this.segments.map((segment) => ({
                    title: segment.name,
                    dataIndex: segment._id,
                    key: segment._id,
                })),
            ];
        },
        getRows() {
            const { buckets, timeSeriesType } = this.data;
            const rows = buckets.map((bucket) => {
                const row = {
                    date: moment(bucket.date).format("DD/MM/YYYY"),
                };
                this.segments.forEach((segment) => {
                    row[segment._id] = bucket.segmentedVisitors[segment._id] || 0;
                });

                return row;
            });
            return rows;
        },
        getStats() {
            const { buckets } = this.data;

            let segmentCounts = buckets.reduce(
                (acc, bucket) => {
                    Object.keys(bucket.segmentedVisitors).forEach((segmentId) => {
                        acc[segmentId] = acc[segmentId] || 0;
                        acc[segmentId] += bucket.segmentedVisitors[segmentId];
                    });
                    return acc;
                },
                {
                    ...this.segments.reduce((acc, segment) => {
                        acc[segment._id] = 0;
                        return acc;
                    }, {}),
                }
            );

            return {
                segmentCounts,
            };
        },
        getXAxisData() {
            const { buckets, timeSeriesType } = this.data;

            return buckets.map((item) => {
                if (timeSeriesType === "daily") {
                    return moment(item.date).format("DD/MM");
                } else if (timeSeriesType === "hourly") {
                    return moment(item.date).format("HH:mm");
                } else {
                    //monthly
                    return moment(item.date).format("MMM");
                }
            });
        },
        getSeries() {
            const { buckets, timeSeriesType } = this.data;

            return this.legends.map((segment, i) => {
                const data = buckets.map((bucket) => {
                    const value = bucket.segmentedVisitors[segment._id];
                    return value || 0;
                });
                return {
                    name: segment.legend,
                    type: "line",
                    stack: "Total",
                    smooth: true,
                    lineStyle: {
                        width: 0,
                    },
                    showSymbol: false,
                    areaStyle: {
                        opacity: 0.8,
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            {
                                offset: 0,
                                color: this.colors[i % this.colors.length],
                            },
                            {
                                offset: 1,
                                color: "rgb(255, 255, 255)",
                            },
                        ]),
                    },
                    emphasis: {
                        focus: "series",
                    },
                    data,
                };
            });
        },
        getSegmentNameById(id) {
            return this.segments.find((x) => x._id === id)?.name;
        },
        exportToExcel() {
            const { buckets, timeSeriesType } = this.data;
            const data = buckets.map((bucket) => {
                const row = {
                    date: moment(bucket.date).format("DD/MM/YYYY"),
                };
                this.segments.forEach((segment) => {
                    const segmentName = this.getSegmentNameById(segment._id);
                    row[segmentName] = bucket.segmentedVisitors[segment._id] || 0;
                });
                return row;
            });
            expertToExcel(data, `segment_timeseries.xlsx`, "Sheet 1");
        },
    },
    watch: {
        data: {
            handler: function (val, oldVal) {
                this.stats = this.getStats();
            },
            deep: true,
        },
        segments: {
            handler: function (val, oldVal) {
                const distinctSegments = _.chain(this.data.buckets)
                    .flatMap((x) => Object.keys(x.segmentedVisitors))
                    .uniq()
                    .map((segmentId) => {
                        this.segments.find((x) => x.id === segmentId)?.name;
                    })
                    .value();
                this.legends = distinctSegments;
                this.colors = distinctSegments.map((x, i) => this.colorList[i % this.colorList.length]);
            },
            deep: true,
        },
    },
});
</script>

<style scoped lang="scss">
.segment-chart {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .stacked-area-chart {
        height: 500px !important;
    }

    .toolbar {
        display: flex;
        justify-content: space-between;

        .left {
            display: flex;

            .buttons {
                display: flex;
                border: 1px solid var(--color-border-default);

                .icon-button {
                    padding: 5px;
                    width: 40px;
                    height: 34px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border-right: 1px solid var(--color-border-default);
                    border-radius: unset !important;

                    &.selected {
                        background-color: var(--color-bg-info-low-hover);

                        i {
                            color: rgb(55, 162, 255);
                        }
                    }

                    &:hover {
                        background-color: var(--color-bg-neutral-pressed);
                    }
                }

                .icon-button:last-child {
                    border-right: none;
                }
            }
        }

        .right {
            display: flex;
            justify-content: flex-end;
        }
    }

    .stats {
        display: flex;
        flex-direction: row;
        gap: 10px;
        flex-wrap: wrap;

        .stat-card {
            flex: 1 1 25%;
            max-height: 115px;
        }
    }
}
</style>
